import React, { useEffect, useMemo, useRef } from "react";
import { Text, FileUpload, FileProgress } from "@ucl/react-library";
import { Trans, useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

import { ChallengeDocument, ChallengeFormValues } from "src/types/challenge-form-types";
import { translationMapping } from "src/utils/translation-mappings";
import { InlineAlertMessage } from "src/components/inline-alert-message/inline-alert-message";

type DocumentUploadProps = {
  resetUploader?: () => void;
};

function fileId(f: File) {
  return `${f.name}__${f.size}__${f.lastModified}`;
}

const allowedFileTypes = [
  "image/heic",
  "image/heif",
  "image/jpg",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
  "image/bmp",
];

export default function DocumentUpload({ resetUploader }: DocumentUploadProps) {
  const { t } = useTranslation();
  const { watch, setValue, clearErrors, setError, formState } = useFormContext<ChallengeFormValues>();

  const maxFileSizeMb = 8;
  const maxFilesCount = 20;
  const artificialMaxFileCount = 25;

  const [showError, setShowError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState({ title: "", body: <></> });

  const hasReceivedCallbackRef = useRef(false);

  const documents = watch("documents") ?? [];

  const helperText = useMemo(() => {
    return t("documentUpload.helperText", {
      maxFileSizeMb,
      count: maxFilesCount,
    });
  }, [t]);

  const exampleDocuments = useMemo(
    () => [
      t("documentUpload.examples.financeAgreements"),
      t("documentUpload.examples.initialDisclosure"),
    ],
    [t]
  );

  useEffect(() => {
    const docErrors = formState.errors.documents;
    if (docErrors?.type === "manual" && docErrors.message === "numOfFiles") {
      setShowError(true);
    }
  }, [formState.errors.documents]);

  //todo tidy up and make sub functions then use the custom file validator
  const fileValidator = (file: File)=> {
    if(file.size / (1024 * 1024) >= maxFileSizeMb) {
      setError("documents", {
        type: "manual",
        message: "fileSize",
      });
      setShowError(true);
      setErrorMessage({
        title: t("errors.fileSize.title", { maxFileSizeMb, fileName: file.name }),
        body: <Trans i18nKey={"errors.fileSize.body"} components={translationMapping(t)} />,
      });
      return {
        valid: false,
        message: "",
      };
    }
    if (!allowedFileTypes.includes(file.type)) {
      setError("documents", {
        type: "manual",
        message: "fileType",
      });
      setShowError(true);
      setErrorMessage({
        title: t("errors.fileType.title", {fileName: file.name}),
        body: <Trans i18nKey={"errors.fileType.body"} components={translationMapping(t)} />,
      });
      return {
        valid: false,
        message: "",
      };
    }
    clearErrors("documents");
    return true;
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="bg-white p-3">
        <Text.P size={"large"}>{t("documentUpload.examplesTitle")}</Text.P>

        <ul className="mt-3 list-disc space-y-2 pl-5 text-[#001A70]">
          {exampleDocuments.map((item) => (
            <li key={item}>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <Text.P className={"pt-4"} size={"large"}>{t("documentUpload.storageStatement")}</Text.P>
      </div>

      <div className="rounded-2xl border border-slate-300 bg-white p-6 sm:p-8">
        <Text.P fontWeight="semi-bold">{t("documentUpload.title")}</Text.P>

        <div className="mt-2">
          <Text.P size="small" className="text-slate-600">
            {helperText}
          </Text.P>
        </div>

        <div className="mt-6">
          <FileUpload
            uniqueIdentifier="doc-upload-additional-info"
            skipUpload
            maxFilesCount={artificialMaxFileCount}
            allowedFileType={allowedFileTypes}
            fileValidator={fileValidator}
            className="fileUploader"
            uploadStatusCallback={(items: any[]) => {
              if (!Array.isArray(items)) return;

              const isFirstCallback = !hasReceivedCallbackRef.current;
              hasReceivedCallbackRef.current = true;

              const nextFiles: File[] = items.map((it) => it.fileObj).filter((f): f is File => f instanceof File);
              if (nextFiles.length > maxFilesCount) {
                const excessFiles = nextFiles.length - maxFilesCount;
                setError("documents", {
                  type: "manual",
                  message: "numOfFiles",
                });
                setShowError(true);
                setErrorMessage({
                  title: t("errors.numOfFiles.title", { maxFilesCount, excessFiles }),
                  body: <Trans i18nKey={"errors.numOfFiles.body"} components={translationMapping(t)} />,
                });
                return;
              } else {
                clearErrors("documents");
                setShowError(false);
              }

              if (nextFiles.length === 0) {
                // Ignore only the very first callback on mount/remount if we
                // already have documents in form state (widget re-initializing).
                // Any later empty callback means the user deleted every file
                // via the widget itself, so we must actually clear the form.
                if (isFirstCallback && documents.length > 0) {
                  return;
                }

                setValue("documents", [], {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                clearErrors("documents");
                return;
              }

              const nextDocuments: ChallengeDocument[] = nextFiles.map((file) => {
                const existing = documents.find((doc) => doc.id === fileId(file));

                return (
                  existing ?? {
                    id: fileId(file),
                    file,
                    documentType: "",
                  }
                );
              });

              setValue("documents", nextDocuments, {
                shouldDirty: true,
                shouldValidate: true,
              });

              clearErrors("documents");
            }}
          >
            <FileUpload.Button buttonLabel={t("documentUpload.browse")} />
            <FileProgress uniqueIdentifier="doc-upload-additional-info" />
          </FileUpload>
          {showError && (
            <InlineAlertMessage
              title={errorMessage.title}
              body={errorMessage.body}
              variant="error"
              onClose={() => {
                setShowError(false);
              }}
            />
          )}
        </div>

        <div className="mt-6">
          <button
            type="button"
            className="text-sm text-[#001A70] underline"
            onClick={() => {
              setValue("documents", [], {
                shouldDirty: true,
                shouldValidate: true,
              });
              resetUploader?.();
              hasReceivedCallbackRef.current = false;
              setError("documents", {
                type: "required",
                message: "chooseAtLeastOneFile",
              });
            }}
          >
            {t("documentUpload.clear")}
          </button>
        </div>
      </div>
    </div>
  );
}
